export const isPromotionActive = (
  now: Date,
  fromDateString: string,
  toDateString: string,
): boolean => {
  if (!fromDateString || !toDateString) return false;

  const from = new Date(fromDateString);
  const to = new Date(toDateString);

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return false;
  }

  const toInclusive = new Date(to);
  if (
    to.getHours() === 0 &&
    to.getMinutes() === 0 &&
    to.getSeconds() === 0 &&
    to.getMilliseconds() === 0
  ) {
    toInclusive.setHours(23, 59, 59, 999);
  }

  return now >= from && now <= toInclusive;
};

/* Parses date string in format dd/MM/yyyy and time string in format HH:mm into a native JS Date object.*/
export const parsePromotionDateTime = (date: string, time: string): Date | null => {
  if (!date || !time) return null;

  const dateParts = date.split("/");
  if (dateParts.length !== 3) return null;

  const [day, month, year] = dateParts;
  const [hours, minutes] = time.split(":");

  const parsedDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    0,
    0
  );

  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

/**
 * Derives the dynamic status of a promotion given the client date,
 * start date/time, end date/time, and isActive status from API.
 */
export const getLivePromotionStatus = (
  isActive: string,
  startDateStr: string,
  startTimeStr: string,
  endDateStr: string,
  endTimeStr: string,
  now: Date
): "Active" | "Inactive" => {
  if (isActive !== "Y") return "Inactive";
  const start = parsePromotionDateTime(startDateStr, startTimeStr);
  const end = parsePromotionDateTime(endDateStr, endTimeStr);
  if (!start || !end) return "Inactive";
  if (now >= start && now <= end) {
    return "Active";
  }
  return "Inactive";
};

/**
 * Parses a client-side promotion date string (which can be "dd/MM/yyyy", "dd/MM/yyyy HH:mm", or "dd/MM/yyyy - HH:mm")
 * into a native JavaScript Date object.
 */
export const parseClientPromotionDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  // Handle ISO format
  if (dateStr.includes("T")) {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  // Handle dd/MM/yyyy or dd-MM-yyyy with optional time suffix
  const cleanStr = dateStr.replace(/\s+-\s+/, " ").trim();
  const parts = cleanStr.split(" ");
  const datePart = parts[0];
  const timePart = parts[1] || "00:00";

  // Handle slashes and dashes dynamically
  const separators = ["/", "-"];
  let dateParts: string[] = [];
  for (const sep of separators) {
    if (datePart.includes(sep)) {
      dateParts = datePart.split(sep);
      break;
    }
  }

  if (dateParts.length !== 3) {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  let year, month, day;
  // If first part has 4 digits, it's YYYY-MM-DD or YYYY/MM/DD
  if (dateParts[0].length === 4) {
    [year, month, day] = dateParts;
  } else {
    // Default to DD/MM/YYYY or DD-MM-YYYY
    [day, month, year] = dateParts;
  }

  const [hours, minutes] = timePart.split(":");

  const parsedDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours || 0),
    Number(minutes || 0),
    0,
    0
  );

  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

/**
 * Checks if a client-side promotion is active based on start/end dates and current client time.
 */
/**
 * Returns true when the promotion's global usage cap has been fully consumed.
 * limitOfUsage = 0 means no cap.
 * Checks availableUsage first (server-computed remainder), then falls back to
 * usageUsed (raw counter) when availableUsage is absent from the response.
 */
export const isPromoUsageLimitReached = (promo: any): boolean => {
  const limit = promo?.limitOfUsage;
  // 0 / null / undefined → unlimited
  if (!limit || limit <= 0) return false;
  // Prefer server-computed remainder (availableUsage = limitOfUsage - usageUsed)
  if (promo.availableUsage !== undefined && promo.availableUsage !== null) {
    return promo.availableUsage <= 0;
  }
  // Fallback: raw counter returned by some endpoints (bulk-promotion, product promotions)
  if (promo.usageUsed !== undefined && promo.usageUsed !== null) {
    return promo.usageUsed >= limit;
  }
  return false;
};

export const isClientPromotionActive = (
  promo: any,
  now: Date
): boolean => {
  if (!promo) return false;
  if (promo.isActive === "N") return false;

  const start = parseClientPromotionDate(promo.startDate);
  const end = parseClientPromotionDate(promo.endDate);

  if (!start || !end) return false;

  // If the end date does not have an explicit time suffix or ISO 'T' symbol,
  // default it to the end of the day (23:59:59.999) instead of midnight (00:00:00.000).
  const endWithTime = new Date(end);
  const hasNoTimeSuffix = !promo.endDate.includes(":") && !promo.endDate.includes("T");
  if (hasNoTimeSuffix) {
    endWithTime.setHours(23, 59, 59, 999);
  }

  return now >= start && now <= endWithTime;
};


